import React, { useState } from 'react'
import { X, Copy, Check, CircleCheck, Share2 } from 'lucide-react'

interface ShareCodeModalProps {
  isOpen: boolean
  agencyCode: string
  agencyName: string
  onClose: () => void
}

export const ShareCodeModal: React.FC<ShareCodeModalProps> = ({
  isOpen,
  agencyCode,
  agencyName,
  onClose
}) => {
  const [copyClicked, setCopyClicked] = useState(false)

  const inviteUrl = `${window.location.origin}/agency/join/${agencyCode}`

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl)
      setCopyClicked(true)

      // Reset the copy button after 3 seconds
      setTimeout(() => {
        setCopyClicked(false)
      }, 3000)
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = inviteUrl
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopyClicked(true)

      setTimeout(() => {
        setCopyClicked(false)
      }, 3000)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg max-w-md w-full mx-4 border border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
              <Share2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Share Agency Code</h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Share this invite link with agents to let them join your agency.
            </p>

            <div className="flex w-full">
              <input
                value={inviteUrl}
                readOnly
                className="flex-grow px-3 py-2 border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white rounded-l-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                placeholder="Invite URL"
              />
              <button
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-tr-lg rounded-br-lg rounded-tl-none rounded-bl-none transition-colors flex items-center gap-2"
                onClick={handleCopyToClipboard}
              >
                {!copyClicked && <Copy className="w-4 h-4" />}
                {copyClicked && <Check className="w-4 h-4" />}
              </button>
            </div>

            {copyClicked && (
              <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 rounded-lg flex items-center gap-2">
                <CircleCheck className="w-4 h-4" />
                <span>Your invite link has been copied!</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ShareCodeModal 